import {
  DataModelBuilder, SerializationContext, SerializedData, cloneData, isSameData,
} from '../../models/dto/dto.js';
import { Address } from '../../models/serializable/address.js';
import { CustomerLocation } from '../../models/serializable/customer-location.js';
import { GeoCoordinates } from '../../models/serializable/geo-coordinates.js';
import { TimeZoneRegion } from '../../models/serializable/time-zone-region.js';
import { toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';
import { VersionRange } from '../../models/core/version-range.js';

export class CustomerLocationComponent extends Component {
  static readonly TYPE_ID: string = '5c3777f2aede4a51bdfcc01900a1b8f1';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly location: CustomerLocation,
  ) {
    super();
  }

  get $typeId(): string {
    return CustomerLocationComponent.TYPE_ID;
  }

  get $removable(): boolean {
    return false;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(CustomerLocationComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CustomerLocationComponent {
    return new CustomerLocationComponent(
        context.fromData<CustomerLocation>(MapUtils.getRecord(map, 'location'))
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'location': context.toData(this.location),
    };
  }

  clone(): this {
    return new CustomerLocationComponent(
        cloneData(this.location),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.location, this.location);
  }
}

export class CustomerLocationComponentBuilder extends DataModelBuilder<CustomerLocationComponent> {
  build(context: SerializationContext, map: SerializedData): CustomerLocationComponent {
    return CustomerLocationComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(CustomerLocationComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CustomerLocationComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    // In version 2 the [location] field was introduced.
    if(version < 2) {
      const address = context.fromDataOrNull<Address>(MapUtils.getRecordOrNull(map, 'address'));
      const coordinates = context.fromDataOrNull<GeoCoordinates>(MapUtils.getRecordOrNull(map, 'coordinates'));
      const timezone = toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'timezone'), TimeZoneRegion);

      const location = new CustomerLocation(address, coordinates, timezone);
      map['location'] = context.toData(location);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
